import React, { useEffect, useState } from "react";
import { ShieldCheck, ArrowRight, Loader2, ChevronLeft, Mail } from "lucide-react";
import { Captcha } from "./Captcha";

function fmtErr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof d === "object") return d.message || JSON.stringify(d);
  return String(d);
}

// Inline "step 2" of the auth flow. The parent hides its form and renders this in
// its place, so it reads as a second step rather than a modal overlay.
//
// onAttempt(captcha, extra) contract:
//   - resolve { done: true } | { close: true }  -> parent navigates / closes step
//   - throw axios error -> this step interprets detail:
//       detail.mfa_required   -> switch to MFA step, refresh captcha, stay
//       detail.captcha_required -> refresh captcha, stay
//       else -> show message, refresh captcha, stay
export function CaptchaStep({
  onBack,
  onAttempt,
  title = "Quick security check",
  subtitle = "Complete the challenge below to continue.",
  submitLabel = "Verify & continue",
  theme = {},
}) {
  const t = {
    title: "text-white",
    text: "text-white/55",
    input:
      "w-full rounded-lg px-4 h-12 text-[14px] text-white bg-white/[0.04] border border-white/12 outline-none focus:border-white/40 placeholder:text-white/30",
    accent: "#c9a227",
    btnText: "#000",
    link: "text-white/60 hover:text-white",
    ...theme,
  };

  const [captcha, setCaptcha] = useState(null);
  const [cKey, setCKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mfaMode, setMfaMode] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    setCaptcha(null); setCKey((k) => k + 1); setBusy(false); setError("");
    setMfaMode(false); setMfaCode(""); setRecoveryCode(""); setUseRecovery(false);
  }, []);

  const refresh = () => { setCaptcha(null); setCKey((k) => k + 1); };

  const canSubmit =
    (mfaMode ? (useRecovery ? recoveryCode.trim() : mfaCode.trim())
             : !!(captcha && captcha.captcha_answer && captcha.captcha_answer.trim())) &&
    !busy;

  const submit = async (e) => {
    e && e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError("");
    const extra = mfaMode
      ? useRecovery ? { recovery_code: recoveryCode.trim() } : { mfa_code: mfaCode.trim() }
      : {};
    try {
      await onAttempt(captcha, extra);
      if (onBack) onBack();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.mfa_required) {
        setMfaMode(true); setUseRecovery(!!d.recovery);
        setError(fmtErr(d) || "Enter the code from your authenticator app.");
      } else if (d && typeof d === "object" && d.captcha_required) {
        setError("That verification was incorrect — please try the new challenge.");
      } else {
        setError(fmtErr(d) || err?.message || "Something went wrong.");
      }
      refresh();
      setBusy(false);
    }
  };

  return (
    <div className="w-full" data-testid="captcha-step">
      <button type="button" onClick={onBack} data-testid="captcha-back"
        className={`inline-flex items-center gap-1.5 text-[12.5px] mb-5 transition-colors ${t.link}`}>
        <ChevronLeft size={15} /> Back
      </button>

      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${t.accent}22`, border: `1px solid ${t.accent}55` }}>
          <ShieldCheck size={17} style={{ color: t.accent }} />
        </span>
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: t.accent }}>{mfaMode ? "Step 3 of 3" : "Step 2 of 2"}</p>
          <h3 className={`font-display text-[17px] font-semibold tracking-tight leading-tight ${t.title}`}>
            {mfaMode ? "Two-factor verification" : title}
          </h3>
        </div>
      </div>
      <p className={`text-[13px] leading-relaxed mb-5 ${t.text}`}>
        {mfaMode ? "Enter the 6-digit code from your authenticator app to finish signing in." : subtitle}
      </p>

      <form onSubmit={submit} className="space-y-4">
        {mfaMode && (
          <div className="space-y-1.5" data-testid="captcha-step-mfa">
            {!useRecovery ? (
              <input data-testid="captcha-step-mfa-code" inputMode="numeric" maxLength={6} autoFocus
                placeholder="6-digit authenticator code" value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${t.input} text-center tracking-widest`} />
            ) : (
              <input data-testid="captcha-step-recovery-code" placeholder="Recovery code (XXXX-XXXX-XXXX)"
                value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
                className={`${t.input} text-center`} />
            )}
            <button type="button" onClick={() => setUseRecovery((v) => !v)} data-testid="captcha-step-toggle-recovery"
              className={`text-[12px] transition-colors ${t.link}`} style={{ color: t.accent }}>
              {useRecovery ? "Use an authenticator code instead" : "Can't use your authenticator? Use a recovery code"}
            </button>
          </div>
        )}

        {!mfaMode && <Captcha key={cKey} onChange={setCaptcha} inputClass={t.input} label="Type the characters" />}

        {error && <div className="text-[13px] text-red-400" data-testid="captcha-step-error">{error}</div>}

        <button type="submit" disabled={!canSubmit} data-testid="captcha-dialog-submit"
          className="w-full h-12 rounded-lg text-[14px] font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
          style={{ background: t.btnBg || t.accent, color: t.btnText }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <>{submitLabel} <ArrowRight size={16} /></>}
        </button>
      </form>
    </div>
  );
}

// Inline "verify your email" step. Shown AFTER the captcha passes on account
// creation: the backend has emailed a 6-digit code, the user types it here, then
// onSubmit(code) actually creates the account. Styled to match CaptchaStep.
//
// onSubmit(code): resolve -> parent advances; throw axios error -> show message, stay.
// onResend(): re-request a code (parent calls POST /auth/signup/resend-code).
export function VerifyCodeStep({
  onBack,
  onSubmit,
  onResend,
  email,
  title = "Verify your email",
  submitLabel = "Verify & continue",
  theme = {},
}) {
  const t = {
    title: "text-white",
    text: "text-white/55",
    input:
      "w-full rounded-lg px-4 h-12 text-[14px] text-white bg-white/[0.04] border border-white/12 outline-none focus:border-white/40 placeholder:text-white/30",
    accent: "#c9a227",
    btnText: "#000",
    link: "text-white/60 hover:text-white",
    ...theme,
  };

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resentMsg, setResentMsg] = useState("");

  const canSubmit = code.trim().length === 6 && !busy;

  const submit = async (e) => {
    e && e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(""); setResentMsg("");
    try {
      await onSubmit(code.trim());
    } catch (err) {
      setError(fmtErr(err?.response?.data?.detail) || err?.message || "That code didn't work.");
      setBusy(false);
    }
  };

  const resend = async () => {
    setError(""); setResentMsg("");
    let throttled = false;
    try { const res = await onResend(); throttled = !!res?.data?.throttled; } catch { /* never reveal */ }
    setResentMsg(throttled ? "Please wait a few seconds before requesting another code." : "We sent a new code to your email.");
    if (!throttled) setCode("");
  };

  return (
    <div className="w-full" data-testid="verify-code-step">
      <button type="button" onClick={onBack} data-testid="verify-code-back"
        className={`inline-flex items-center gap-1.5 text-[12.5px] mb-5 transition-colors ${t.link}`}>
        <ChevronLeft size={15} /> Back
      </button>

      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${t.accent}22`, border: `1px solid ${t.accent}55` }}>
          <Mail size={17} style={{ color: t.accent }} />
        </span>
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: t.accent }}>Verify your email</p>
          <h3 className={`font-display text-[17px] font-semibold tracking-tight leading-tight ${t.title}`}>{title}</h3>
        </div>
      </div>
      <p className={`text-[13px] leading-relaxed mb-5 ${t.text}`}>
        We emailed a 6-digit code{email ? <> to <span className="text-white/80 font-medium">{email}</span></> : ""}. Enter it below to finish. The code expires in 10 minutes.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <input data-testid="verify-code-input" inputMode="numeric" maxLength={6} autoFocus
          placeholder="Enter 6-digit code" value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className={`${t.input} text-center tracking-[0.5em] text-[18px]`} />

        {error && <div className="text-[13px] text-red-400" data-testid="verify-code-error">{error}</div>}
        {resentMsg && <div className="text-[13px]" style={{ color: t.accent }} data-testid="verify-code-resent">{resentMsg}</div>}

        <button type="submit" disabled={!canSubmit} data-testid="verify-code-submit"
          className="w-full h-12 rounded-lg text-[14px] font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-50"
          style={{ background: t.btnBg || t.accent, color: t.btnText }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <>{submitLabel} <ArrowRight size={16} /></>}
        </button>

        <button type="button" onClick={resend} data-testid="verify-code-resend"
          className={`w-full text-center text-[12.5px] transition-colors ${t.link}`}>
          Didn't get it? Resend code
        </button>
      </form>
    </div>
  );
}

export default CaptchaStep;
